import { forwardRef, useState, useCallback } from 'react'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { TextFieldProps } from '@mui/material/TextField'

export const ExternalLabel = styled(Typography)(({ theme }) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '16px',
    fontWeight: 400,
    color: 'rgba(21, 56, 98, 0.5)',
    paddingLeft: '5px',
    paddingRight: '8px',
    marginBottom: '6px'
}))

type UInputProps = TextFieldProps & { labelFontSize?: string }

export const UInput = forwardRef<HTMLDivElement, UInputProps>(({ label, autoComplete, type, value, slotProps, labelFontSize, ...props }, ref) => {
    const isPassword = type === 'password'
    const testId = (props as { 'data-testid'?: string })['data-testid']

    // Chrome/Edge ignore autoComplete="off" and even "new-password" on
    // password-type inputs: the saved-password dropdown still pops on
    // click. The reliable way to block that is to hide the field from the
    // browser's password-manager heuristic on mount by rendering it as a
    // plain text input, then swap to the real password type once the user
    // focuses. By that point Chrome has already skipped autofill.
    const [passwordTouched, setPasswordTouched] = useState(false)

    const hasValue = typeof value === 'string' ? value.length > 0 : value != null
    const effectiveType = isPassword && !passwordTouched && !hasValue ? 'text' : type

    // Password-type fields also leak through form history. Use random
    // autocomplete tokens so the browser cannot correlate them with a
    // saved credential bucket.
    const defaultAutoComplete = isPassword || effectiveType === 'text' ? 'new-password' : 'off'

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (isPassword && !passwordTouched) setPasswordTouched(true)
            const callerOnFocus = (slotProps?.htmlInput as { onFocus?: React.FocusEventHandler<HTMLInputElement> } | undefined)?.onFocus
            callerOnFocus?.(e)
        },
        [isPassword, passwordTouched, slotProps]
    )

    const mergedHtmlInput = isPassword
        ? {
              ...(slotProps?.htmlInput as object | undefined),
              onFocus: handleFocus
          }
        : slotProps?.htmlInput

    return (
        <Box sx={{ width: '100%', flex: 1, minWidth: 0 }} data-testid={testId ? `${testId}-container` : 'uinput-box-base-styled-input'}>
            {label && (
                <ExternalLabel
                    style={labelFontSize ? { fontSize: labelFontSize } : undefined}
                    data-testid={testId ? `${testId}-label` : 'uinput-external-label'}>
                    {label}
                </ExternalLabel>
            )}
            <BaseStyledInput
                ref={ref}
                type={effectiveType}
                value={value}
                autoComplete={autoComplete ?? defaultAutoComplete}
                slotProps={{
                    ...slotProps,
                    htmlInput: mergedHtmlInput
                }}
                {...props}
                data-testid={testId ?? 'uinput-base-styled-input'}
            />
        </Box>
    )
})
UInput.displayName = 'UInput'

const BaseStyledInput = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-root': {
        height: 50,
        borderRadius: 6,
        backgroundColor: '#ffffff',
        fontFamily: theme.typography.fontFamily,
        fontSize: 14,
        color: '#153862'
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: '1px solid rgba(153, 188, 223, 0.25)'
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(153, 188, 223, 0.25)'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(153, 188, 223, 0.25)',
        borderWidth: 1
    },
    '& .MuiInputBase-input': {
        fontFamily: theme.typography.fontFamily,
        fontSize: 14,
        fontWeight: 400,
        color: '#153862',
        padding: '14px 16px'
    },
    '& .MuiInputAdornment-root .MuiTypography-root': {
        fontFamily: theme.typography.fontFamily,
        fontSize: 14,
        fontWeight: 400,
        color: '#153862'
    },
    '& .MuiInputBase-root.Mui-disabled': {
        backgroundColor: '#ffffff',
        color: '#153862',
        WebkitTextFillColor: '#153862'
    },
    '& .MuiInputBase-input.Mui-disabled': {
        color: '#153862',
        WebkitTextFillColor: '#153862'
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(153, 188, 223, 0.25)'
    }
}))
